/**
 * Landing page variant: A — Filmstrip Pipeline
 * Converted from design prototype wireframe JSX.
 */
import React from "react";
import { Link } from "react-router-dom";
import { HOME_LINE } from "../public/legal-text.ts";
import {
  WfNavBar, WfFooter, FooterCTA, FaqRow,
  PlayBlob, FakeWaveform, ScriptLines, 
} from "./primitives.tsx";

// Wireframe A — "Filmstrip / Pipeline Tape"
// Hero IS the pipeline: topic → script → storyboard → render, shown as a literal filmstrip.

export function WireframeA() {
  return (
    <div className="wf paper" style={{ maxWidth: 1280, width: "100%", fontFamily: "Patrick Hand, sans-serif" }}>
      <WfNavBar />

      {/* HERO */}
      <section style={{ padding: "60px 60px 30px", position: "relative" }}>

        <h1 style={{ fontSize: 110, lineHeight: 0.92, letterSpacing: "-1px", maxWidth: 1100 }}>
          ONE TOPIC GOES IN.<br />
          A WHOLE <span className="hl" style={{ display: "inline-block", transform: "none" }}>YOUTUBE&nbsp;VIDEO</span><br />
          COMES OUT.
        </h1>
        {/* (B120-1; D5) the line under the headline */}
        <p data-home-line="" style={{ fontSize: 20, lineHeight: 1.45, maxWidth: 720, margin: "18px 0 0" }}>{HOME_LINE}</p>

        <div className="row" style={{ alignItems: "center", gap: 22, marginTop: 28 }}>
          <Link to="/signup" className="btn accent" style={{ fontSize: 22, textDecoration: "none" }}>▶ start with 2 free</Link>
          <div className="hand mute" style={{ fontSize: 20 }}>no card · no edit skills · just pick a topic</div>
        </div>
      </section>

      {/* FILMSTRIP */}
      <section style={{ padding: "30px 0 50px", position: "relative" }}>
        <div style={{ background: "var(--paper-2)", border: "1px solid var(--rule-2)", borderRadius: 16, padding: "24px 24px", position: "relative" }}>
          <div className="row" style={{ gap: 16, alignItems: "stretch", overflow: "hidden" }}>
            {/* Frame 1 — TOPIC */}
            <FilmFrame stage="01" tag="TOPIC">
              <div className="hand" style={{ fontSize: 28, color: "var(--ink)" }}>"Top 10<br/>weirdest deep-sea<br/>creatures"</div>
              <div className="row" style={{ gap: 6, marginTop: 12, flexWrap:"wrap" }}>
                {["Reddit","Top 10","History","Motivation","Shorts"].map((t,i) =>
                  <span key={t} className="pill" style={{ fontSize: 10, padding: "3px 8px", background: i===1?"var(--highlight)":"transparent" }}>{t}</span>
                )}
              </div>
            </FilmFrame>

            <FilmFrame stage="02" tag="SCRIPT · GATE 1">
              <ScriptLines lines={7} />
              <div className="row" style={{ gap: 6, marginTop: 10, alignItems:"center" }}>
                <FakeWaveform width={140} height={20} bars={22} seed={3} />
                <span className="label" style={{ fontSize: 9 }}>2:14</span>
              </div>
              <div className="hand accent" style={{ fontSize: 18, marginTop: 6 }}>↑ edit any line</div>
            </FilmFrame>

            <FilmFrame stage="03" tag="STORYBOARD · GATE 2">
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap: 6 }}>
                {Array.from({ length: 6 }).map((_, i) =>
                  <div key={i} className="thumb" style={{ height: 36 }}><div style={{ position:"absolute", bottom:2, left:4, fontFamily:"Special Elite", fontSize:9 }}>seg {i+1}</div></div>
                )}
              </div>
              <div className="hand accent" style={{ fontSize: 18, marginTop: 6 }}>↻ regen any frame</div>
            </FilmFrame>

            <FilmFrame stage="04" tag="RENDER">
              <div className="thumb" style={{ height: 100, background: "#2a2a2a" }}>
                <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", zIndex:3 }}>
                  <PlayBlob size={42} />
                </div>
                <div style={{ position:"absolute", bottom:4, right:6, background:"var(--ink)", color:"var(--paper)", fontFamily:"Special Elite", fontSize:9, padding:"1px 4px", zIndex:3 }}>1080p</div>
              </div>
              <div className="row" style={{ gap: 6, marginTop: 8 }}>
                <span className="pill" style={{ fontSize: 9, padding:"2px 6px" }}>↓ MP4</span>
                <span className="pill" style={{ fontSize: 9, padding:"2px 6px", background:"var(--accent)", color:"var(--ink)" }}>▶ Publish</span>
              </div>
            </FilmFrame>

          </div>
        </div>

      </section>

      {/* HOW IT WORKS — expanded */}
      <a id="how-it-works" />
      <section style={{ padding: "70px 60px", borderTop: "1px solid var(--rule-2)", background: "var(--paper-2)" }}>
        <div className="row" style={{ alignItems: "flex-end", justifyContent: "space-between", marginBottom: 30 }}>
          <h2 style={{ fontSize: 64, lineHeight: 0.9 }}>HOW THE<br/>FORGE WORKS.</h2>
          <div className="hand mute" style={{ fontSize: 22, maxWidth: 340, textAlign: "right" }}>two gates. you sign off. nothing renders till you say so.</div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 18 }}>
          {[
            ["01","PICK","Choose a niche preset. Voice + image model + caption style come baked in.","🪄"],
            ["02","SCRIPT","Hook · body · CTA written in your tone. Edit any line, regen any section.","✍︎"],
            ["03","STORYBOARD","Every segment gets an AI frame. Regen the bad ones. Or rewrite the prompt.","🖼"],
            ["04","RENDER","Voiceover + captions + Ken-Burns motion. MP4 down OR straight to YT.","📺"],
          ].map(([n,t,d]) => (
            <div key={n} className="box flat" style={{ padding: 24, background: "#fff" }}>
              <div style={{ fontFamily:"var(--mono)", fontSize: 12, letterSpacing: "0.1em", color: "var(--accent)", fontWeight: 500 }}>{n} / 04</div>
              <div className="display" style={{ fontSize: 28, marginTop: 18, letterSpacing: "-0.02em" }}>{t}</div>
              <div className="type mute" style={{ marginTop: 8, fontSize: 14, lineHeight: 1.5 }}>{d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* LIVE PEEK */}
      <section style={{ padding: "70px 60px", borderTop: "1px solid var(--rule-2)", position:"relative" }}>
        <h2 style={{ fontSize: 56, lineHeight: 0.95, maxWidth: 900 }}>WATCH A SCRIPT TURN INTO PIXELS, IN REAL TIME.</h2>
        <div className="row" style={{ gap: 18, marginTop: 28, alignItems:"stretch" }}>
          {/* mock app browser */}
          <div className="box flat" style={{ flex: 1, padding: 0, background:"#fafafa", overflow: "hidden" }}>
            <div className="screen-bar"><i/><i/><i/><span className="label" style={{ marginLeft: 8 }}>app.tubeforge — gate 2 storyboard</span></div>
            <div className="row" style={{ padding: 16, gap: 14 }}>
              <div style={{ flex: 1 }}>
                <div className="label">SEGMENT 03 OF 12</div>
                <ScriptLines lines={3} />
                <div className="row" style={{ gap: 8, marginTop: 14 }}>
                  <span className="pill" style={{ fontSize: 10 }}>↻ regen image</span>
                  <span className="pill" style={{ fontSize: 10 }}>✎ edit prompt</span>
                  <span className="pill" style={{ fontSize: 10 }}>✓ approve</span>
                </div>
              </div>
              <div className="thumb" style={{ width: 280, height: 160 }} />
            </div>
            <div style={{ borderTop: "1px solid var(--rule-2)", padding:"10px 16px", display:"flex", justifyContent:"space-between" }}>
              <div className="row" style={{ gap: 4 }}>
                {Array.from({ length: 12 }).map((_,i) =>
                  <div key={i} style={{ width:36, height: 22, background: i<5? "var(--accent)" : "rgba(0,0,0,0.08)", border: "1px solid var(--rule-2)" }} />
                )}
              </div>
              <span className="hand">5/12 approved</span>
            </div>
          </div>

          <div className="col" style={{ width: 360, gap: 14 }}>
            <div className="box flat" style={{ padding: 18 }}>
              <div className="label">WHY IT'S DIFFERENT</div>
              <ul className="type" style={{ marginTop: 10, paddingLeft: 18, fontSize: 18, lineHeight: 1.5 }}>
                <li>You see <span className="hl">every word</span> before render.</li>
                <li>You see <span className="hl">every frame</span> before render.</li>
                <li>Failed render? <b>Credits refund automatically.</b></li>
              </ul>
            </div>
          </div>
        </div>
      </section>


      {/* FAQ */}
      <a id="faq" />
      <section style={{ padding: "70px 60px", borderTop: "1px solid var(--rule-2)" }}>
        <h2 style={{ fontSize: 56, marginBottom: 24 }}>QUESTIONS YOU'D ASK A FRIEND.</h2>
        <div className="row" style={{ gap: 40 }}>
          <div style={{ flex: 1 }}>
            <FaqRow q="How long does one video really take?" open />
            <FaqRow q="What if I hate the script?" />
            <FaqRow q="Can I publish straight to YouTube?" />
            <FaqRow q="What happens when a render fails?" />
            <FaqRow q="Do you do Shorts too?" />
            <FaqRow q="Cancel anytime?" />
          </div>
          <div style={{ width: 280 }}>
            <div className="hand" style={{ fontSize: 28, lineHeight: 1.1 }}>Still curious?</div>
            <div className="type mute" style={{ fontSize: 16, marginTop: 8 }}>Drop us a line. Real humans.</div>
            <div className="btn ghost" style={{ marginTop: 16, fontSize: 16 }}>✉ hi@tubeforge.dev</div>
          </div>
        </div>
      </section>

      <FooterCTA />
      <WfFooter />
    </div>
  );
}

function FilmFrame({ stage, tag, children }: { stage: string; tag: string; children: React.ReactNode }) {
  return (
    <div className="box flat" style={{ width: 260, background: "#fff", padding: 16, position: "relative" }}>
      <div className="row" style={{ justifyContent:"space-between", marginBottom: 12, alignItems:"baseline" }}>
        <span className="label" style={{ color: "var(--ink-soft)" }}>{tag}</span>
        <span style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 500, letterSpacing: "0.1em", color: "var(--accent)" }}>{stage}</span>
      </div>
      <div style={{ minHeight: 150 }}>{children}</div>
    </div>
  );
}
